import { useEffect, useState } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Container,
  Group,
  Paper,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconBrandGoogleDrive, IconRefresh, IconSearch } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../api/client";
import type { DriveFolder, DriveStatus } from "../api/types";
import { ResponsiveTable } from "../components/responsiveTable/ResponsiveTable";

export function Drive() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<DriveStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [folders, setFolders] = useState<DriveFolder[]>([]);
  const [folderQuery, setFolderQuery] = useState("");
  const [searching, setSearching] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      setStatus(await api.driveStatus());
    } catch {
      notifications.show({ color: "red", message: t("drive.statusLoadFailed") });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();

    const params = new URLSearchParams(window.location.search);
    const driveParam = params.get("drive");
    if (driveParam === "connected") {
      notifications.show({ color: "green", message: t("drive.connected") });
    } else if (driveParam === "error") {
      const reason = params.get("reason") || "";
      notifications.show({
        color: "red",
        title: "OAuth",
        message: reason ? `${t("drive.connectFailed")} (${reason})` : t("drive.connectFailed"),
      });
    }
    if (driveParam) {
      params.delete("drive");
      params.delete("reason");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status?.authenticated && folders.length === 0) {
      void searchFolders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.authenticated]);

  async function handleConnect() {
    setConnecting(true);
    try {
      const { auth_url } = await api.driveConnect();
      window.location.href = auth_url;
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      const msg =
        detail && typeof detail === "object" && "detail" in detail
          ? String((detail as Record<string, unknown>).detail)
          : t("drive.connectFailed");
      notifications.show({ color: "red", title: "OAuth", message: msg });
      setConnecting(false);
    }
  }

  async function handleDisconnect() {
    setDisconnecting(true);
    try {
      const s = await api.driveDisconnect();
      setStatus(s);
      setFolders([]);
      notifications.show({ color: "green", message: t("drive.disconnected") });
    } catch {
      notifications.show({ color: "red", message: t("drive.disconnectFailed") });
    } finally {
      setDisconnecting(false);
    }
  }

  async function searchFolders() {
    setSearching(true);
    try {
      setFolders(await api.driveFolders(folderQuery || undefined));
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      const msg =
        detail && typeof detail === "object" && "error" in detail
          ? String((detail as Record<string, unknown>).error)
          : t("drive.listFoldersFailed");
      notifications.show({ color: "red", message: msg });
    } finally {
      setSearching(false);
    }
  }

  function statusBadge() {
    if (loading) return <Badge color="gray">{t("drive.statusLoading")}</Badge>;
    if (!status?.configured) return <Badge color="red">{t("drive.statusNoCredentials")}</Badge>;
    if (!status.authenticated) return <Badge color="yellow">{t("drive.statusDisconnected")}</Badge>;
    if (status.expired) return <Badge color="blue">{t("drive.statusExpired")}</Badge>;
    return <Badge color="green">{t("drive.statusConnected")}</Badge>;
  }

  return (
    <Container size="xl">
      <Group justify="space-between" mb="md">
        <Title order={2}>{t("drive.title")}</Title>
        <Button
          variant="subtle"
          leftSection={<IconRefresh size={14} />}
          onClick={refresh}
          loading={loading}
        >
          {t("drive.refresh")}
        </Button>
      </Group>

      <Paper withBorder p="lg" mb="lg">
        <Group justify="space-between" mb="sm">
          <Group>
            <IconBrandGoogleDrive size={28} />
            <div>
              <Text fw={500}>{t("drive.statusLabel")}</Text>
              {statusBadge()}
            </div>
          </Group>
          <Group gap="xs">
            {status?.configured && !status?.authenticated && (
              <Button onClick={handleConnect} loading={connecting}>
                {t("drive.connectButton")}
              </Button>
            )}
            {status?.authenticated && (
              <Button color="red" variant="light" onClick={handleDisconnect} loading={disconnecting}>
                {t("drive.disconnectButton")}
              </Button>
            )}
          </Group>
        </Group>

        {status && status.message && (
          <Alert color="yellow" mt="md">
            {status.message}
          </Alert>
        )}
        {status && !status.configured && (
          <Alert color="red" mt="md" title={t("drive.missingCredentialsTitle")}>
            {t("drive.missingCredentialsBodyPre")}<code>backend/credentials/credentials.json</code>{" "}
            {t("drive.missingCredentialsBodyPost")}
          </Alert>
        )}
        {status?.credentials_path && (
          <Text size="xs" c="dimmed" mt="xs">
            credentials: <code>{status.credentials_path}</code>
          </Text>
        )}
      </Paper>

      {status?.authenticated && (
        <Paper withBorder p="lg">
          <Title order={4} mb="sm">
            {t("drive.foldersTitle")}
          </Title>
          <Group mb="md">
            <TextInput
              placeholder={t("drive.searchPlaceholder")}
              leftSection={<IconSearch size={14} />}
              value={folderQuery}
              onChange={(e) => setFolderQuery(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") searchFolders();
              }}
              w={320}
              maw="100%"
            />
            <Button onClick={searchFolders} loading={searching}>
              {t("drive.searchButton")}
            </Button>
          </Group>

          <ResponsiveTable
            mobile={{
              rows: folders,
              rowKey: (f) => f.id,
              title: (f) => f.name,
              subtitle: (f) => f.modifiedTime ?? "—",
              fields: [{ label: "ID", render: (f) => <Text ff="monospace" size="xs">{f.id}</Text> }],
              onOpen: (f) => { window.open(`https://drive.google.com/drive/folders/${f.id}`, "_blank", "noopener,noreferrer"); },
              empty: t("drive.noFolders"),
            }}
          >
          <Table striped withTableBorder highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("drive.colName")}</Table.Th>
                <Table.Th>ID</Table.Th>
                <Table.Th>{t("drive.colModified")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {folders.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={3}>
                    <Text c="dimmed">{t("drive.noFolders")}</Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {folders.map((f) => (
                <Table.Tr key={f.id}>
                  <Table.Td>
                    <Anchor
                      href={`https://drive.google.com/drive/folders/${f.id}`}
                      target="_blank"
                      fw={500}
                    >
                      {f.name}
                    </Anchor>
                  </Table.Td>
                  <Table.Td>
                    <Text ff="monospace" size="xs">
                      {f.id}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {f.modifiedTime ?? "—"}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          </ResponsiveTable>
        </Paper>
      )}
    </Container>
  );
}
